import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    KNOWN_ENTITY_TYPES, HEIGHTS, BUILDS, SKIN_COLOURS, SKIN_TEXTURES,
    EYE_SHAPES, PUPIL_TYPES, EYE_COUNTS, HEAD_SHAPES, MOUTH_TYPES, NOSE_TYPES,
    FINGER_COUNTS, LIMB_COUNTS, LEG_STRUCTURES, BODY_SHAPES, HAIR_OPTIONS,
    CLOTHING_OPTIONS, MOVEMENT_STYLES, COMMUNICATION_STYLES, EMOTIONAL_ATMOSPHERE,
    SOUND_OPTIONS, SMELL_OPTIONS, VIBRATION_OPTIONS, TECHNOLOGY_OPTIONS,
    CRAFT_TYPES, EVIDENCE_LEVELS, REALITY_TYPES, ENCOUNTER_TYPES, ORIGIN_SYSTEMS,
} from "@/data/formOptions";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Upload, Send, CheckCircle2, Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";

const STAGES = [
    "Basic Report",
    "Entity Description",
    "Craft Details",
    "Evidence",
    "Review & Submit",
];

const initial = {
    title: "",
    what_happened: "",
    age_at_time: "",
    location: "",
    country: "",
    encounter_date: "",
    witnesses: "",
    saw_craft: false,
    saw_being: false,
    telepathy: false,
    missing_time: false,
    physical_marks: false,
    reality_type: "unknown",
    encounter_type: "sighting",
    entity_known_type: "",
    entity_custom_name: "",
    height: "",
    build: "",
    skin_colour: "",
    skin_texture: "",
    eye_shape: "",
    pupil_type: "",
    eye_count: "",
    head_shape: "",
    mouth_type: "",
    nose_type: "",
    finger_count: "",
    limb_count: "",
    leg_structure: "",
    body_shape: "",
    hair: "",
    clothing: "",
    symbols: "",
    movement: "",
    communication: "",
    emotional_atmosphere: "",
    sound: "",
    smell: "",
    vibration: "",
    technology: "",
    origin_star: "",
    planet_claimed: "",
    dimension_claimed: "",
    other_details: "",
    craft_type: "",
    craft_size: "",
    craft_color: "",
    craft_lights: "",
    evidence_level: "testimony",
    evidence_urls: [],
    evidence_files: [],
    anonymous: false,
};

export default function Lodge() {
    const [stage, setStage] = useState(0);
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const navigate = useNavigate();
    const startedRef = useRef(false);

    const set = (k, v) => {
        if (!startedRef.current) {
            startedRef.current = true;
            track("encounter_submission_started");
        }
        setForm((f) => ({ ...f, [k]: v }));
    };

    const next = () => {
        if (stage === 0 && form.what_happened.trim().length < 10) {
            return toast.error("Please describe what happened (at least 10 characters)");
        }
        setStage((s) => Math.min(STAGES.length - 1, s + 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const back = () => { setStage((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

    const uploadFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/uploads/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
            // Push full metadata (file_id, stored_name, url, filename, file_type, file_kind, size, ...)
            // so the create_encounter handler can attach + gate the file. Old code pushed only the URL.
            set("evidence_files", [...(form.evidence_files || []), data]);
            set("evidence_urls", [...(form.evidence_urls || []), data.url]); // keep legacy field too
            toast.success("Evidence uploaded");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setUploading(false); }
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                age_at_time: form.age_at_time ? parseInt(form.age_at_time, 10) : null,
                evidence_files: form.evidence_files || [],
            };
            const { data } = await api.post("/encounters", payload);
            // Only fire on confirmed success. We send only the new encounter id —
            // never the report text or any free-form witness content.
            track("encounter_submission_completed", { encounter_id: data.id });
            toast.success("Encounter logged. Pending moderator review. View it any time in My Submissions.");
            navigate(`/encounters/${data.id}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="ok-container py-10 max-w-4xl mx-auto" data-testid="lodge-page">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50">Lodge Encounter</div>
            <h1 className="font-display text-3xl sm:text-4xl mt-1 mb-6">Submit your report</h1>

            {/* Progress */}
            <div className="flex items-center gap-2 mb-8" data-testid="lodge-progress">
                {STAGES.map((label, i) => (
                    <div key={label} className="flex-1">
                        <div className={`h-1 rounded-full transition-all ${i <= stage ? "bg-[var(--ok-accent)]" : "bg-[var(--ok-border)]"}`} />
                        <div className={`mt-2 font-mono text-[10px] uppercase tracking-widest ${i === stage ? "text-[var(--ok-accent)]" : "opacity-50"}`}>
                            {i + 1}. {label}
                        </div>
                    </div>
                ))}
            </div>

            {stage === 0 && <Stage1 form={form} set={set} />}
            {stage === 1 && <Stage2 form={form} set={set} />}
            {stage === 2 && <Stage3 form={form} set={set} />}
            {stage === 3 && <Stage4 form={form} set={set} uploadFile={uploadFile} uploading={uploading} />}
            {stage === 4 && <Stage5 form={form} />}

            <div className="flex items-center justify-between mt-10">
                <button onClick={back} disabled={stage === 0} className="ok-btn-outline disabled:opacity-30" data-testid="lodge-back-btn">
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
                {stage < STAGES.length - 1 ? (
                    <button onClick={next} className="ok-btn-primary" data-testid="lodge-next-btn">
                        Next <ArrowRight className="w-4 h-4" />
                    </button>
                ) : (
                    <button onClick={submit} disabled={submitting} className="ok-btn-primary" data-testid="lodge-submit-btn">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Submit Encounter
                    </button>
                )}
            </div>
        </div>
    );
}

function Field({ label, hint, children, testId }) {
    return (
        <div className="space-y-1.5" data-testid={testId}>
            <label className="font-mono text-[10px] uppercase tracking-widest opacity-60">{label}</label>
            {children}
            {hint && <p className="text-[11px] opacity-50">{hint}</p>}
        </div>
    );
}

function Dropdown({ value, onChange, options, testId, placeholder = "Select…" }) {
    return (
        <select value={value} onChange={(e) => onChange(e.target.value)} className="ok-input text-sm" data-testid={testId}>
            <option value="">{placeholder}</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    );
}

function DropdownOther({ value, onChange, options, testId }) {
    const isOther = value && !options.includes(value);
    return (
        <div className="space-y-1.5">
            <select
                value={isOther ? "Other" : value}
                onChange={(e) => onChange(e.target.value === "Other" ? "" : e.target.value)}
                className="ok-input text-sm"
                data-testid={testId}
            >
                <option value="">Select…</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {(isOther || value === "" && false) || isOther ? (
                <input
                    type="text"
                    value={isOther ? value : ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Describe manually…"
                    className="ok-input text-sm"
                    data-testid={`${testId}-other`}
                />
            ) : null}
        </div>
    );
}

function Toggle({ label, value, onChange, testId }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!value)}
            className={`ok-card p-3 text-left transition-all ${value ? "border-[var(--ok-accent)]" : ""}`}
            data-testid={testId}
        >
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{label}</span>
                <span className={`w-9 h-5 rounded-full transition-all relative ${value ? "bg-[var(--ok-accent)]" : "bg-[var(--ok-border)]"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${value ? "left-4" : "left-0.5"}`} />
                </span>
            </div>
        </button>
    );
}

function Stage1({ form, set }) {
    return (
        <div className="space-y-6" data-testid="lodge-stage-1">
            <Field label="What happened?" hint="Describe in your own words. This is the heart of your report.">
                <textarea
                    rows={6}
                    value={form.what_happened}
                    onChange={(e) => set("what_happened", e.target.value)}
                    placeholder="On the evening of…"
                    className="ok-input text-sm"
                    data-testid="lodge-what-happened"
                />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="How old were you?"><input type="number" min="0" max="120" value={form.age_at_time} onChange={(e) => set("age_at_time", e.target.value)} className="ok-input text-sm" data-testid="lodge-age" /></Field>
                <Field label="Date / approximate year"><input type="text" value={form.encounter_date} onChange={(e) => set("encounter_date", e.target.value)} placeholder="YYYY-MM-DD or 1996" className="ok-input text-sm" data-testid="lodge-date" /></Field>
                <Field label="Where were you?"><input type="text" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City, region, country" className="ok-input text-sm" data-testid="lodge-location" /></Field>
                <Field label="Who was with you?"><input type="text" value={form.witnesses} onChange={(e) => set("witnesses", e.target.value)} placeholder="Alone, family, friends…" className="ok-input text-sm" data-testid="lodge-witnesses" /></Field>
                <Field label="Encounter type"><Dropdown value={form.encounter_type} onChange={(v) => set("encounter_type", v)} options={ENCOUNTER_TYPES} testId="lodge-encounter-type" /></Field>
                <Field label="Reality type"><Dropdown value={form.reality_type} onChange={(v) => set("reality_type", v)} options={REALITY_TYPES} testId="lodge-reality-type" /></Field>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Toggle label="Saw a craft" value={form.saw_craft} onChange={(v) => set("saw_craft", v)} testId="lodge-toggle-craft" />
                <Toggle label="Saw a being" value={form.saw_being} onChange={(v) => set("saw_being", v)} testId="lodge-toggle-being" />
                <Toggle label="Telepathy" value={form.telepathy} onChange={(v) => set("telepathy", v)} testId="lodge-toggle-telepathy" />
                <Toggle label="Missing time" value={form.missing_time} onChange={(v) => set("missing_time", v)} testId="lodge-toggle-missing-time" />
                <Toggle label="Physical marks" value={form.physical_marks} onChange={(v) => set("physical_marks", v)} testId="lodge-toggle-physical-marks" />
                <Toggle label="Submit anonymously" value={form.anonymous} onChange={(v) => set("anonymous", v)} testId="lodge-toggle-anonymous" />
            </div>
        </div>
    );
}

function Stage2({ form, set }) {
    return (
        <div className="space-y-6" data-testid="lodge-stage-2">
            <Field label="Known entity type" hint="Select a known archetype, or choose Other to describe freely.">
                <Dropdown value={form.entity_known_type} onChange={(v) => set("entity_known_type", v)} options={KNOWN_ENTITY_TYPES} testId="lodge-entity-known-type" />
            </Field>
            {(form.entity_known_type === "Other" || form.entity_known_type === "Unknown") && (
                <Field label="Custom entity name"><input value={form.entity_custom_name} onChange={(e) => set("entity_custom_name", e.target.value)} className="ok-input text-sm" data-testid="lodge-entity-custom-name" /></Field>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Height"><DropdownOther value={form.height} onChange={(v) => set("height", v)} options={HEIGHTS} testId="lodge-height" /></Field>
                <Field label="Build"><DropdownOther value={form.build} onChange={(v) => set("build", v)} options={BUILDS} testId="lodge-build" /></Field>
                <Field label="Skin colour"><DropdownOther value={form.skin_colour} onChange={(v) => set("skin_colour", v)} options={SKIN_COLOURS} testId="lodge-skin-colour" /></Field>
                <Field label="Skin texture"><DropdownOther value={form.skin_texture} onChange={(v) => set("skin_texture", v)} options={SKIN_TEXTURES} testId="lodge-skin-texture" /></Field>
                <Field label="Eye shape"><DropdownOther value={form.eye_shape} onChange={(v) => set("eye_shape", v)} options={EYE_SHAPES} testId="lodge-eye-shape" /></Field>
                <Field label="Pupil type"><DropdownOther value={form.pupil_type} onChange={(v) => set("pupil_type", v)} options={PUPIL_TYPES} testId="lodge-pupil-type" /></Field>
                <Field label="Eye count"><DropdownOther value={form.eye_count} onChange={(v) => set("eye_count", v)} options={EYE_COUNTS} testId="lodge-eye-count" /></Field>
                <Field label="Head shape"><DropdownOther value={form.head_shape} onChange={(v) => set("head_shape", v)} options={HEAD_SHAPES} testId="lodge-head-shape" /></Field>
                <Field label="Mouth"><DropdownOther value={form.mouth_type} onChange={(v) => set("mouth_type", v)} options={MOUTH_TYPES} testId="lodge-mouth-type" /></Field>
                <Field label="Nose"><DropdownOther value={form.nose_type} onChange={(v) => set("nose_type", v)} options={NOSE_TYPES} testId="lodge-nose-type" /></Field>
                <Field label="Fingers"><DropdownOther value={form.finger_count} onChange={(v) => set("finger_count", v)} options={FINGER_COUNTS} testId="lodge-finger-count" /></Field>
                <Field label="Limbs"><DropdownOther value={form.limb_count} onChange={(v) => set("limb_count", v)} options={LIMB_COUNTS} testId="lodge-limb-count" /></Field>
                <Field label="Leg structure"><DropdownOther value={form.leg_structure} onChange={(v) => set("leg_structure", v)} options={LEG_STRUCTURES} testId="lodge-leg-structure" /></Field>
                <Field label="Body shape"><DropdownOther value={form.body_shape} onChange={(v) => set("body_shape", v)} options={BODY_SHAPES} testId="lodge-body-shape" /></Field>
                <Field label="Hair"><DropdownOther value={form.hair} onChange={(v) => set("hair", v)} options={HAIR_OPTIONS} testId="lodge-hair" /></Field>
                <Field label="Clothing"><DropdownOther value={form.clothing} onChange={(v) => set("clothing", v)} options={CLOTHING_OPTIONS} testId="lodge-clothing" /></Field>
                <Field label="Movement"><DropdownOther value={form.movement} onChange={(v) => set("movement", v)} options={MOVEMENT_STYLES} testId="lodge-movement" /></Field>
                <Field label="Communication"><DropdownOther value={form.communication} onChange={(v) => set("communication", v)} options={COMMUNICATION_STYLES} testId="lodge-communication" /></Field>
                <Field label="Emotional atmosphere"><DropdownOther value={form.emotional_atmosphere} onChange={(v) => set("emotional_atmosphere", v)} options={EMOTIONAL_ATMOSPHERE} testId="lodge-emotional-atmosphere" /></Field>
                <Field label="Sound"><DropdownOther value={form.sound} onChange={(v) => set("sound", v)} options={SOUND_OPTIONS} testId="lodge-sound" /></Field>
                <Field label="Smell"><DropdownOther value={form.smell} onChange={(v) => set("smell", v)} options={SMELL_OPTIONS} testId="lodge-smell" /></Field>
                <Field label="Vibration / body sensation"><DropdownOther value={form.vibration} onChange={(v) => set("vibration", v)} options={VIBRATION_OPTIONS} testId="lodge-vibration" /></Field>
                <Field label="Technology used"><DropdownOther value={form.technology} onChange={(v) => set("technology", v)} options={TECHNOLOGY_OPTIONS} testId="lodge-technology" /></Field>
                <Field label="Symbols / markings"><input value={form.symbols} onChange={(e) => set("symbols", e.target.value)} className="ok-input text-sm" data-testid="lodge-symbols" /></Field>
                <Field label="Origin / star system"><DropdownOther value={form.origin_star} onChange={(v) => set("origin_star", v)} options={ORIGIN_SYSTEMS} testId="lodge-origin-star" /></Field>
                <Field label="Planet claimed"><input value={form.planet_claimed} onChange={(e) => set("planet_claimed", e.target.value)} className="ok-input text-sm" data-testid="lodge-planet" /></Field>
                <Field label="Dimension / realm claimed"><input value={form.dimension_claimed} onChange={(e) => set("dimension_claimed", e.target.value)} className="ok-input text-sm" data-testid="lodge-dimension" /></Field>
            </div>
            <Field label="Other details">
                <textarea rows={3} value={form.other_details} onChange={(e) => set("other_details", e.target.value)} className="ok-input text-sm" data-testid="lodge-other-details" />
            </Field>
        </div>
    );
}

function Stage3({ form, set }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lodge-stage-3">
            <Field label="Craft type"><DropdownOther value={form.craft_type} onChange={(v) => set("craft_type", v)} options={CRAFT_TYPES} testId="lodge-craft-type" /></Field>
            <Field label="Craft size"><input value={form.craft_size} onChange={(e) => set("craft_size", e.target.value)} className="ok-input text-sm" data-testid="lodge-craft-size" /></Field>
            <Field label="Craft colour"><input value={form.craft_color} onChange={(e) => set("craft_color", e.target.value)} className="ok-input text-sm" data-testid="lodge-craft-color" /></Field>
            <Field label="Lights / pattern"><input value={form.craft_lights} onChange={(e) => set("craft_lights", e.target.value)} className="ok-input text-sm" data-testid="lodge-craft-lights" /></Field>
        </div>
    );
}

function Stage4({ form, set, uploadFile, uploading }) {
    return (
        <div className="space-y-6" data-testid="lodge-stage-4">
            <Field label="Evidence level"><Dropdown value={form.evidence_level} onChange={(v) => set("evidence_level", v)} options={EVIDENCE_LEVELS} testId="lodge-evidence-level" /></Field>
            <div className="ok-card p-6 text-center">
                <Upload className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <div className="text-sm mb-3 opacity-80">Upload photos, videos, audio or documents (max 25MB each)</div>
                <label className="ok-btn-primary cursor-pointer inline-flex" data-testid="lodge-upload-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? "Uploading…" : "Upload file"}
                    <input type="file" onChange={uploadFile} className="hidden" data-testid="lodge-upload-input" />
                </label>
                {form.evidence_urls?.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-2">
                        {form.evidence_urls.map((u) => (
                            <div key={u} className="text-[10px] font-mono opacity-70 truncate p-2 ok-card">{u.split("/").pop()}</div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Stage5({ form }) {
    return (
        <div className="space-y-4" data-testid="lodge-stage-5">
            <div className="ok-card p-6">
                <div className="font-display text-xl mb-2">{form.title || (form.entity_known_type || "Anomalous") + " encounter"}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-3">
                    {form.encounter_date || "Date unknown"} · {form.location || "Location unknown"}
                </div>
                <p className="text-sm opacity-80 whitespace-pre-wrap">{form.what_happened}</p>
            </div>
            <div className="ok-card p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[var(--ok-accent)] flex-shrink-0 mt-0.5" />
                <div className="text-sm opacity-80">
                    On submission, the archive will generate a clinical AI sketch from your description, plus an
                    analyst-style summary, tags, and an initial credibility score. These can be refined by the community.
                </div>
            </div>
        </div>
    );
}
